import React, { useMemo, useRef, useEffect, useState } from 'react';
import { User } from 'lucide-react';

// Heritage color palette
const COLORS = {
  heritageGreen: '#2C4A3B',
  warmSand: '#E6E2D3',
  goldAccent: '#C9A227',
  darkText: '#1a1a1a',
  lightText: '#f5f5f5',
  maleColor: '#2C4A3B',
  femaleColor: '#4A2C3B',
  unknownColor: '#3B3B2C'
};

// Fan Chart - Circular ancestor visualization
const FanChart = ({ 
  individuals = [], 
  relationships = [], 
  rootPersonId,
  onPersonClick,
  maxGenerations = 5
}) => {
  const canvasRef = useRef(null);
  const [hoveredPerson, setHoveredPerson] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 800 });
  
  // Build ancestor tree
  const ancestorData = useMemo(() => {
    if (!rootPersonId || individuals.length === 0) return null;
    
    const indById = {};
    individuals.forEach(ind => { indById[ind.id] = ind; });
    
    // Build parent lookup
    const childToParents = {};
    relationships.forEach(rel => {
      if (rel.relationship_category === 'parent_child' && rel.child_id && rel.parent_id) {
        if (!childToParents[rel.child_id]) {
          childToParents[rel.child_id] = [];
        }
        childToParents[rel.child_id].push(rel.parent_id);
      }
    });
    
    // Helper to extract year
    const extractYear = (dateStr) => {
      if (!dateStr) return null;
      const match = dateStr.match(/(\d{4})/);
      return match ? parseInt(match[1]) : null;
    };
    
    // Build ancestor array by generation
    const generations = [[]];
    const visited = new Set();
    
    // Start with root
    const root = indById[rootPersonId];
    if (!root) return null;
    
    generations[0].push({
      id: root.id,
      name: `${root.given_names || ''} ${root.surname || ''}`.trim(),
      gender: root.sex ?? root.gender,
      birthYear: extractYear(root.birth_date),
      generation: 0,
      position: 0
    });
    visited.add(root.id);
    
    // Build each generation
    for (let gen = 0; gen < maxGenerations; gen++) {
      const nextGen = [];
      const currentGen = generations[gen] || [];
      
      currentGen.forEach((person, idx) => {
        const parents = childToParents[person.id] || [];
        
        // Ensure we have exactly 2 slots per person (for proper fan layout)
        for (let i = 0; i < 2; i++) {
          const parentId = parents[i];
          if (parentId && !visited.has(parentId)) {
            const parent = indById[parentId];
            if (parent) {
              visited.add(parentId);
              nextGen.push({
                id: parent.id,
                name: `${parent.given_names || ''} ${parent.surname || ''}`.trim(),
                gender: parent.sex ?? parent.gender,
                birthYear: extractYear(parent.birth_date),
                generation: gen + 1,
                position: idx * 2 + i,
                childPosition: idx
              });
            } else {
              nextGen.push(null); // Placeholder for unknown ancestor
            }
          } else {
            nextGen.push(null); // Placeholder
          }
        }
      });
      
      if (nextGen.some(p => p !== null)) {
        generations.push(nextGen);
      }
    }
    
    return generations;
  }, [individuals, relationships, rootPersonId, maxGenerations]);
  
  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      const container = canvasRef.current?.parentElement;
      if (container) {
        const size = Math.min(container.clientWidth, container.clientHeight, 900);
        setDimensions({ width: size, height: size });
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Draw the fan chart
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !ancestorData) return;
    
    const ctx = canvas.getContext('2d');
    const { width, height } = dimensions;
    const centerX = width / 2;
    const centerY = height / 2;
    const baseRadius = Math.min(width, height) * 0.08;
    const ringWidth = Math.min(width, height) * 0.1;
    
    // Clear canvas
    ctx.fillStyle = COLORS.warmSand;
    ctx.fillRect(0, 0, width, height);
    
    // Draw each generation ring
    ancestorData.forEach((generation, genIndex) => {
      if (genIndex === 0) {
        // Draw root person as center circle
        const person = generation[0];
        if (person) {
          ctx.beginPath();
          ctx.arc(centerX, centerY, baseRadius, 0, Math.PI * 2);
          ctx.fillStyle = person.gender === 'male' ? COLORS.maleColor : 
                         person.gender === 'female' ? COLORS.femaleColor : 
                         COLORS.unknownColor;
          ctx.fill();
          ctx.strokeStyle = COLORS.goldAccent;
          ctx.lineWidth = 3;
          ctx.stroke();
          
          // Draw name
          ctx.fillStyle = COLORS.lightText;
          ctx.font = 'bold 11px system-ui';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          const nameParts = person.name.split(' ');
          if (nameParts.length > 1) {
            ctx.fillText(nameParts[0], centerX, centerY - 6);
            ctx.fillText(nameParts.slice(1).join(' '), centerX, centerY + 8);
          } else {
            ctx.fillText(person.name, centerX, centerY);
          }
        }
        return;
      }
      
      const innerRadius = baseRadius + (genIndex - 1) * ringWidth + 10;
      const outerRadius = innerRadius + ringWidth - 5;
      const segmentCount = Math.pow(2, genIndex);
      const anglePerSegment = (Math.PI * 2) / segmentCount;
      
      generation.forEach((person, idx) => {
        if (!person) return;
        
        const startAngle = -Math.PI / 2 + idx * anglePerSegment;
        const endAngle = startAngle + anglePerSegment;
        
        // Draw segment
        ctx.beginPath();
        ctx.arc(centerX, centerY, outerRadius, startAngle, endAngle);
        ctx.arc(centerX, centerY, innerRadius, endAngle, startAngle, true);
        ctx.closePath();
        
        const isHovered = hoveredPerson === person.id;
        ctx.fillStyle = person.gender === 'male' ? COLORS.maleColor : 
                       person.gender === 'female' ? COLORS.femaleColor : 
                       COLORS.unknownColor;
        if (isHovered) {
          ctx.fillStyle = COLORS.goldAccent;
        }
        ctx.fill();
        
        ctx.strokeStyle = 'rgba(255,255,255,0.3)';
        ctx.lineWidth = 1;
        ctx.stroke();
        
        // Draw name (rotated text)
        const midAngle = (startAngle + endAngle) / 2;
        const textRadius = (innerRadius + outerRadius) / 2;
        const textX = centerX + Math.cos(midAngle) * textRadius;
        const textY = centerY + Math.sin(midAngle) * textRadius;
        
        ctx.save();
        ctx.translate(textX, textY);
        
        // Rotate text to follow arc
        let rotation = midAngle + Math.PI / 2;
        if (midAngle > Math.PI / 2 || midAngle < -Math.PI / 2) {
          rotation += Math.PI;
        }
        ctx.rotate(rotation);
        
        ctx.fillStyle = COLORS.lightText;
        ctx.font = `${Math.max(8, 12 - genIndex)}px system-ui`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Truncate name if needed
        const maxWidth = anglePerSegment * textRadius * 0.8;
        let displayName = person.name;
        while (ctx.measureText(displayName).width > maxWidth && displayName.length > 3) {
          displayName = displayName.slice(0, -1);
        }
        if (displayName !== person.name) displayName += '...';
        
        ctx.fillText(displayName, 0, 0);
        ctx.restore();
      });
    });
    
    // Draw generation labels
    ctx.fillStyle = COLORS.heritageGreen;
    ctx.font = '10px system-ui';
    ctx.textAlign = 'left';
    for (let i = 1; i <= Math.min(ancestorData.length - 1, 5); i++) {
      const labelRadius = baseRadius + (i - 0.5) * ringWidth + 5;
      ctx.fillText(`Gen ${i}`, centerX + labelRadius + 5, centerY - 5);
    }
    
  }, [ancestorData, dimensions, hoveredPerson]);
  
  // Handle mouse events for interactivity
  const handleMouseMove = (e) => {
    if (!ancestorData || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const { width, height } = dimensions;
    const centerX = width / 2;
    const centerY = height / 2;
    const baseRadius = Math.min(width, height) * 0.08;
    const ringWidth = Math.min(width, height) * 0.1;
    
    // Calculate distance and angle from center
    const dx = x - centerX;
    const dy = y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    let angle = Math.atan2(dy, dx) + Math.PI / 2;
    if (angle < 0) angle += Math.PI * 2;
    
    // Check which segment is hovered
    if (distance < baseRadius) {
      // Center (root)
      setHoveredPerson(ancestorData[0]?.[0]?.id || null);
      return;
    }
    
    for (let genIndex = 1; genIndex < ancestorData.length; genIndex++) {
      const innerRadius = baseRadius + (genIndex - 1) * ringWidth + 10;
      const outerRadius = innerRadius + ringWidth - 5;
      
      if (distance >= innerRadius && distance <= outerRadius) {
        const segmentCount = Math.pow(2, genIndex);
        const segmentIndex = Math.floor(angle / (Math.PI * 2 / segmentCount));
        const person = ancestorData[genIndex]?.[segmentIndex];
        setHoveredPerson(person?.id || null);
        return;
      }
    }
    
    setHoveredPerson(null);
  };
  
  const handleClick = (e) => {
    if (hoveredPerson && onPersonClick) {
      onPersonClick(hoveredPerson);
    }
  };
  
  if (!rootPersonId) {
    return (
      <div className="flex items-center justify-center h-full" style={{ backgroundColor: COLORS.warmSand }}>
        <p className="text-lg" style={{ color: COLORS.heritageGreen }}>
          Select a person to view their fan chart
        </p>
      </div>
    );
  }
  
  const rootPerson = individuals.find(i => i.id === rootPersonId);
  
  return (
    <div 
      className="w-full h-full flex flex-col items-center justify-center p-4"
      style={{ backgroundColor: COLORS.warmSand }}
    >
      {/* Chart title */}
      <div className="mb-4 text-center">
        <h3 className="text-lg font-semibold" style={{ color: COLORS.heritageGreen }}>
          Ancestors of {rootPerson ? `${rootPerson.given_names || ''} ${rootPerson.surname || ''}`.trim() : 'Selected Person'}
        </h3>
        <p className="text-sm opacity-70" style={{ color: COLORS.heritageGreen }}>
          Fan Chart View • Click any segment to view details
        </p>
      </div>
      
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        width={dimensions.width}
        height={dimensions.height}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredPerson(null)}
        onClick={handleClick}
        style={{ 
          cursor: hoveredPerson ? 'pointer' : 'default',
          borderRadius: '50%',
          boxShadow: '0 4px 20px rgba(44, 74, 59, 0.2)'
        }}
      />
      
      {/* Legend */}
      <div className="mt-4 flex gap-6 text-sm" style={{ color: COLORS.heritageGreen }}>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS.maleColor }} />
          <span>Male</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS.femaleColor }} />
          <span>Female</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS.unknownColor }} />
          <span>Unknown</span>
        </div>
      </div>
      
      {/* Hovered person info */}
      {hoveredPerson && (
        <div 
          className="mt-4 px-4 py-2 rounded-lg shadow-md"
          style={{ backgroundColor: COLORS.heritageGreen, color: COLORS.warmSand }}
        >
          {(() => {
            const person = individuals.find(i => i.id === hoveredPerson);
            if (!person) return null;
            return (
              <div className="text-center">
                <p className="font-semibold">{`${person.given_names || ''} ${person.surname || ''}`.trim()}</p>
                {person.birth_date && <p className="text-xs opacity-80">b. {person.birth_date.split('-')[0]}</p>}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default FanChart;
